import { forwardRef, type InputHTMLAttributes } from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string
  /**
   * Visually hide the label (it stays in the DOM for assistive tech).
   * Matches AddressAutocomplete's prop of the same name — for fields whose
   * purpose is carried by a placeholder or surrounding context.
   */
  hideLabel?: boolean
  hint?: string
  error?: string
  required?: boolean
  /**
   * Extra classes for the outer wrapper `<div>`. Appended after the
   * defaults (`m-bx3 al-field`) so callers can add layout helpers (e.g.
   * `no-m-b` to remove the default bottom margin, or `flex-grow` inside
   * a row).
   */
  wrapperClassName?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hideLabel, hint, error, required, id, className = '', wrapperClassName = '', ...rest },
  ref,
) {
  const inputId = id ?? `al-input-${Math.random().toString(36).slice(2, 10)}`
  const describedBy = error ? `${inputId}-err` : hint ? `${inputId}-hint` : undefined
  return (
    <div
      className={`m-bx3 al-field ${error ? 'al-field--error' : ''} ${wrapperClassName}`.trim()}
    >
      <LabelPrimitive.Root
        htmlFor={inputId}
        className={`al-field__label ${hideLabel ? 'visuallyhidden' : ''}`.trim()}
      >
        {label}
        {required ? (
          <>
            {' '}
            <span className="al-field__required" aria-hidden="true">
              *
            </span>
          </>
        ) : null}
      </LabelPrimitive.Root>
      <input
        ref={ref}
        id={inputId}
        className={`${className}`}
        aria-invalid={error ? true : undefined}
        aria-required={required ? true : undefined}
        aria-describedby={describedBy}
        {...rest}
      />
      {error ? (
        <p id={`${inputId}-err`} role="alert" className="al-field__error xsmall">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="al-field__hint">
          {hint}
        </p>
      ) : null}
    </div>
  )
})
